<script setup>
import { computed } from "vue";
import CheckCircleBroken from "@/Tailwind/Icon/Duotone/CheckCircleBroken.vue";
import FileCheckTwo from "@/Tailwind/Icon/FileCheckTwo.vue";
import BuildingSix from "@/Tailwind/Icon/Duotone/BuildingSix.vue";
import route from "ziggy-js";
import { useBreakpoints } from "@/Tailwind/Mixins/useBreakpoints";
import ArrowNarrowRight from "@/Tailwind/Icon/ArrowNarrowRight.vue";
import { UserIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  status: Number,
  authenticated: Boolean,
});

const title = computed(() => {
  return {
    404: t("components.errors.404.title"),
    403: t("components.errors.403.title"),
  }[props.status];
});

const description = computed(() => {
  return {
    404: t("components.errors.404.description"),
    403: t("components.errors.403.description"),
  }[props.status];
});

const { isMobile } = useBreakpoints();

const authenticatedLinks = [
  {
    title: t("components.errors.links.tasks.title"),
    description: t("components.errors.links.tasks.description"),
    icon: CheckCircleBroken,
    route: route("tasks.index"),
  },
  {
    title: t("components.errors.links.documents.title"),
    description: t("components.errors.links.documents.description"),
    icon: FileCheckTwo,
    route: route("settings.organisation.notes.index"),
  },
  {
    title: t("components.errors.links.organisation.title"),
    description: t("components.errors.links.organisation.description"),
    icon: BuildingSix,
    route: isMobile
      ? route("settings.organisation.index")
      : route("settings.organisation.buildings"),
  },
];

const anonymousLinks = [
  {
    title: t("components.errors.links.login.title"),
    description: t("components.errors.links.login.description"),
    icon: UserIcon,
    route: route("login"),
  },
];

const links = props.authenticated ? authenticatedLinks : anonymousLinks;
</script>

<template>
  <div class="flex flex-col items-center">
    <div class="absolute top-20">
      <span
        class="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
      >
        <svg
          class="h-1.5 w-1.5 fill-red-500"
          viewBox="0 0 6 6"
          aria-hidden="true"
        >
          <circle cx="3" cy="3" r="3" />
        </svg>
        {{ status }}
      </span>
    </div>

    <div class="absolute -top-4 z-0">
      <svg
        width="768"
        height="494"
        viewBox="0 0 768 494"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_2391_34637"
          style="mask-type: alpha"
          maskUnits="userSpaceOnUse"
          x="0"
          y="-274"
          width="768"
          height="768"
        >
          <rect
            width="768"
            height="768"
            transform="translate(0 -274)"
            fill="url(#paint0_radial_2391_34637)"
          />
        </mask>
        <g mask="url(#mask0_2391_34637)">
          <circle cx="384" cy="110" r="47.5" stroke="#EAECF0" />
          <circle cx="384" cy="110" r="95.5" stroke="#EAECF0" />
          <circle cx="384" cy="110" r="143.5" stroke="#EAECF0" />
          <circle cx="384" cy="110" r="191.5" stroke="#EAECF0" />
          <circle cx="384" cy="110" r="239.5" stroke="#EAECF0" />
          <circle cx="384" cy="110" r="287.5" stroke="#EAECF0" />
          <circle cx="384" cy="110" r="335.5" stroke="#EAECF0" />
          <circle cx="384" cy="110" r="383.5" stroke="#EAECF0" />
        </g>
        <defs>
          <radialGradient
            id="paint0_radial_2391_34637"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(384 384) rotate(90) scale(384 384)"
          >
            <stop />
            <stop offset="1" stop-opacity="0" />
          </radialGradient>
        </defs>
      </svg>
    </div>

    <div class="flex w-full justify-center pt-36 z-10 px-4 sm:px-0">
      <div class="max-w-3xl flex flex-col gap-y-6">
        <div class="flex flex-col gap-y-4 items-center">
          <h1 class="text-3xl text-gray-900 font-semibold">{{ title }}</h1>
          <h2 class="text-lg text-gray-600">{{ description }}</h2>
        </div>
        <div class="flex flex-col divide-y">
          <InertiaLink
            v-for="link in links"
            :key="link.title"
            :href="link.route"
            class="flex justify-between items-center py-6 group"
          >
            <div class="flex gap-4">
              <div
                class="flex shrink-0 items-center justify-center w-11 h-11 border rounded-xl"
              >
                <component :is="link.icon" class="text-gray-900 w-5" />
              </div>
              <div class="flex flex-col">
                <span class="font-semibold text-sm text-gray-900">{{
                  link.title
                }}</span>
                <span class="text-gray-600 text-sm">{{
                  link.description
                }}</span>
              </div>
            </div>
            <div
              class="text-gray-400 group-hover:text-gray-900 group-hover:cursor-pointer"
            >
              <ArrowNarrowRight />
            </div>
          </InertiaLink>
        </div>
      </div>
    </div>
  </div>
</template>
